import React, { useEffect, useState } from "react";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Button, Form, message } from "antd";
import { useNavigate } from "react-router-dom";
import "./style.css";
import InputCard from "../../../../../Components/Cards/InputCard";
import { useLocation } from "react-router-dom";
import {
  getModuleById,
  updateModule,
} from "../../../../../API/Admin/SystemConfiguration/ModulePricing";

const EditModulePrice = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();

  const moduleId = location.state?.moduleId;
  const [activeModule, setActiveModule] = useState(null);
  const [saving, setSaving] = useState(false);
  const currency = Form.useWatch("currencyPreference", form) || "JMD";

  const initialValues = {
    price: 0,
    currencyPreference: "JMD",
    billingCycle: "monthly",
  };

  useEffect(() => {
    if (!moduleId) {
      message.error(
        "No module selected. Please open this page from Module Pricing & Settlements.",
      );
      return;
    }

    const fetchModule = async () => {
      try {
        const res = await getModuleById(moduleId);
        const data = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && data) {
          setActiveModule(data);
          const billingCycle = form.getFieldValue("billingCycle") || "monthly";
          form.setFieldsValue({
            price:
              billingCycle === "annually"
                ? parseFloat(data.annual_price_per_vehicle) || 0
                : parseFloat(data.monthly_price_per_vehicle) || 0,
            currencyPreference: data.currency || "JMD",
          });
        } else {
          message.error(res?.data?.message || "Failed to load module.");
        }
      } catch (err) {
        message.error(
          err?.response?.data?.message || "Failed to load module.",
        );
      }
    };

    fetchModule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId]);

  // The annual price is always monthly × 12, computed by the backend, so it
  // can only be changed by editing the monthly price.
  const billingCycle = Form.useWatch("billingCycle", form);
  const isAnnual = billingCycle === "annually";

  const pricingFields = [
    {
      name: "price",
      label: isAnnual
        ? "Annual Price per Vehicle (Monthly × 12)"
        : "New Price per Vehicle",
      placeholder: "Enter price",
      fullWidth: false,
      type: "number",
      disabled: isAnnual,
      rules: isAnnual ? [] : [{ required: true, message: "Price is required" }],
    },
    {
      name: "currencyPreference",
      label: "Currency Preference",
      placeholder: "Select Currency",
      fullWidth: false,
      type: "currency",
      rules: [{ required: true, message: "Currency is required" }],
    },

    {
      name: "billingCycle",
      label: "Billing Cycle",
      type: "radio",
      options: [
        { label: "Monthly", value: "monthly" },
        { label: "Annually", value: "annually" },
      ],
      fullWidth: true,
    },
  ];

  const handleback = () => {
    navigate("/system-configuration", {
      state: { activeTab: location.state?.from || "Module Pricing & Settlements" },
    });
  };

  const handleCancel = () => {
    form.resetFields();
    if (activeModule) {
      form.setFieldsValue({
        price: parseFloat(activeModule.monthly_price_per_vehicle) || 0,
        currencyPreference: activeModule.currency || "JMD",
      });
    }
  };

  const handleSave = async () => {
    if (!moduleId) {
      message.error("No module selected.");
      return;
    }

    let values;
    try {
      values = await form.validateFields();
    } catch (error) {
      message.error("Please correct the errors in the form.");
      return;
    }

    // Currency preference has no confirmed backend field on the module
    // record, so it isn't sent. Only the monthly price is editable; the
    // backend derives the annual price from it.
    if (values.billingCycle === "annually") return;
    const payload = { monthly_price_per_vehicle: values.price };

    setSaving(true);
    try {
      const res = await updateModule(moduleId, payload);

      if (Math.floor(res?.status / 100) === 2) {
        message.success(res?.data?.message || "Module pricing updated successfully!");
        navigate("/system-configuration", {
          state: { activeTab: location.state?.from || "Module Pricing & Settlements" },
        });
      } else {
        message.error(res?.data?.message || "Failed to update module pricing.");
      }
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to update module pricing.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="edit-module-page">
      <div className="back-btn">
        <Button icon={<ArrowLeftOutlined />} onClick={handleback}>
          Back
        </Button>
      </div>
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onValuesChange={(changed) => {
          if (!changed.billingCycle || !activeModule) return;

          form.setFieldsValue({
            price:
              changed.billingCycle === "annually"
                ? parseFloat(activeModule.annual_price_per_vehicle) || 0
                : parseFloat(activeModule.monthly_price_per_vehicle) || 0,
          });
        }}
      >
        <div className="main-section-title">Price Management</div>
        <div className="price-tab">
          <div className="top-gradient"></div>
          <div className="inner-grey-border-card">
            <div className="sub-card-title">Current Active Pricing</div>
            <div className="price-row">
              <span className="price-text blue">
                {activeModule?.currency || "JMD"}{" "}
                {activeModule?.monthly_price_per_vehicle ?? "0.00"}
              </span>
              <span className="price-unit">/ vehicle / monthly</span>
            </div>
            <div className="price-row">
              <span className="price-text grey">
                {activeModule?.currency || "JMD"}{" "}
                {activeModule?.annual_price_per_vehicle ?? "0.00"}
              </span>
              <span className="price-unit">/ vehicle / annually</span>
            </div>
          </div>
        </div>

        <div>
          <InputCard title="Price Configuration" fields={pricingFields} />
        </div>

        <div className="page-footer-btns">
          <Button onClick={handleCancel} className="btn-secondary">
            Cancel
          </Button>
          <Button
            className="btn-primary"
            onClick={handleSave}
            loading={saving}
            disabled={saving || isAnnual}
          >
            Save Changes
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default EditModulePrice;
